import { Fragment, useState, useEffect } from "react";
import { ScrollRestoration, useSearchParams } from "react-router-dom";
import LoadingPlaceholder from "../../components/ui/loading/LoadingPlaceholder";

function Privacy({ embed = false }) {
  const [searchParams] = useSearchParams();
  const isEmbed = embed || searchParams.get("embed") === "1";
  const [loading, setLoading] = useState(true);

  // Simulate loading for consistency and future API calls
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fragment>
      <ScrollRestoration />
      <main className={isEmbed ? "" : "legal-page"}>
        <div className={isEmbed ? "" : "content legal-content"}>
          {loading ? (
            <LoadingPlaceholder rows={3} type="card-large" />
          ) : (
            <div id="terms">
              {!isEmbed && <h1>Privacy Policy</h1>}
              <p> Date of Last Revision: August 16, 2026</p>
              <p>
                This Privacy Policy explains how Plaintr collects, uses,
                discloses, protects, retains, and deletes information when you
                use the Plaintr website, Android mobile application, and related
                services (collectively, the “Services”).
              </p>
              <p>
                Plaintr is operated by Plaintr LLC, referred to in this Privacy
                Policy as “Plaintr,” “we,” “us,” or “our.”
              </p>
              <h3>1. Information We Collect</h3>
              <p>
                We collect information to provide, improve, and secure our
                services. The information we collect depends on how you use the
                Services and may include information you provide, information
                generated through your use of the Services, and information
                received from services you choose to connect.
              </p>
              <h4>1.1. Personal Information</h4>
              <p>We may collect the following personal data:</p>
              <ol>
                <li>
                  <strong>For Users:</strong> Full name, email address, phone
                  number, and payment details, login or authentication
                  information, communication preferences, and other profile
                  information you provide.
                </li>
                <li>
                  <strong>For Tax Professionals:</strong> Licensing,
                  certifications, professional affiliations, and contact
                  information, including practice areas, credentials, and
                  business contact information.
                </li>
              </ol>
              <h4>1.2. Transaction, Financial, Tax, and Payment Information</h4>
              <ol>
                <li>
                  Details about transactions, service engagements, and payment
                  amounts, including pricing, payment status, and related
                  records.
                </li>
                <li>
                  Depending on the Services you use, we may collect or process
                  financial account information, transaction history, income and
                  expense information, budget information, tax-related
                  information, and documents, images, or other records you
                  choose to provide.
                </li>
                <li>
                  Payments are processed by Stripe. Plaintr does not receive or
                  store complete payment card numbers or bank-account
                  credentials. We may receive limited information from Stripe
                  about a payment, such as the transaction amount and payment
                  status.
                </li>
              </ol>
              <h4>1.3. Communication Records</h4>
              <ol>
                <li>
                  Messages sent through the platform between Users and Tax
                  Professionals.
                </li>
                <li>
                  Customer support interactions and feedback, and other
                  communications you send to us.
                </li>
              </ol>
              <h4>1.4. Website, Android App, Device, and Usage Information</h4>
              <p>
                When you use the Plaintr website or Android application, we may
                collect information needed to operate, secure, understand, and
                support the Services, including:
              </p>
              <ol>
                <li>
                  Device or browser type, platform, and operating-system
                  information.
                </li>
                <li>App or browser version and technical usage information.</li>
                <li>IP address and security-related connection information.</li>
                <li>App or installation identifiers.</li>
                <li>
                  Push-notification registration tokens and preference status,
                  where applicable.
                </li>
                <li>Website access or app activity timestamps.</li>
              </ol>
              <h4>1.5. Authentication and Connected Services</h4>
              <p>
                If you sign in through Google or another supported
                authentication provider, we receive the account identifiers and
                profile information you authorize that provider to share, such
                as your name, email address, and profile image.
              </p>
              <p>
                If you choose to connect a financial account, Plaintr uses Plaid
                to facilitate the connection. Based on the accounts you select
                and the permissions you provide, Plaintr receives account
                details, balances, transaction history, and other financial
                information made available through the connected institution.
                Plaintr uses this information to provide the financial overview,
                insights, and related features you request. Plaintr does not
                receive or store the login credentials you use to access your
                financial institution or complete bank-account numbers.
              </p>
              <h3>2. How We Use Your Information</h3>
              <p>
                We use the information we collect for the following purposes:
              </p>
              <ol>
                <li>
                  <strong>Service Facilitation</strong>
                  <ol>
                    <li>
                      To connect Users with Tax Professionals based on
                      preferences and needs.
                    </li>
                    <li>To process transactions securely.</li>
                  </ol>
                </li>
                <li>
                  <strong>Platform Enhancement</strong>
                  <ol>
                    <li>
                      To analyze platform usage and improve functionality.
                    </li>
                    <li>
                      To ensure system stability and optimize user experience.
                    </li>
                    <li>
                      To troubleshoot technical issues and improve the
                      reliability and functionality of the Services.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Communication</strong>
                  <ol>
                    <li>
                      To send confirmations, reminders, and important
                      notifications about services, filing, accounts, and
                      security.
                    </li>
                    <li>To respond to inquiries and provide support.</li>
                  </ol>
                </li>
                <li>
                  <strong> Compliance</strong>
                  <ol>
                    <li>
                      To meet legal, regulatory, or contractual obligations,
                      including applicable tax and accounting requirements.
                    </li>
                    <li>
                      To enforce our Terms and Conditions and protect the
                      rights, safety, and property of Plaintr, our users, Tax
                      Professionals, and others.
                    </li>
                    <li>
                      To prevent fraud, unauthorized access, and other harmful
                      activity.
                    </li>
                  </ol>
                </li>
              </ol>
              <p>PlainTR does not sell or rent your data to third parties.</p>
              <h3>3. Sharing Your Information</h3>
              <h4>3.1. Tax Professionals and Service Providers</h4>
              <p>
                Plaintr connects clients with independent Tax Professionals,
                including Certified Public Accountants, Enrolled Agents, and Tax
                Attorneys. When you request professional services, we disclose
                the information reasonably needed to match you with a Tax
                Professional and allow that professional to provide the
                requested services.
              </p>
              <p>
                Tax Professionals are responsible for handling client
                information in accordance with their agreement with Plaintr and
                their applicable professional and legal obligations.
              </p>
              <p>
                We share data with trusted third parties essential to our
                services, such as:
              </p>
              <ol>
                <li>Payment processors to handle transactions.</li>
                <li>
                  IT service providers for system maintenance, cloud hosting and
                  storage, authentication, notifications, financial-account
                  connectivity, electronic document and signature services,
                  communications, customer support, analytics, fraud prevention,
                  and technical maintenance.
                </li>
              </ol>
              <p>
                These providers process information for the services they
                provide to Plaintr, subject to their agreements with Plaintr and
                applicable law.
              </p>
              <h4>3.2 Legal and Regulatory Disclosures</h4>
              <p>
                We may disclose your information if required by law, such as to
                comply with court orders or legal processes, regulatory
                requirements, or other applicable legal obligations.
              </p>
              <h4>3.3 Fraud Prevention and Security</h4>
              <p>
                To prevent, detect, or address potential fraud, unauthorized
                access, or illegal activity, we may share information with law
                enforcement or security partners; enforce our agreements; or
                protect the rights, safety, and property of Plaintr, our users,
                Tax Professionals, or others.
              </p>
              <h4>3.4. With Your Consent or at Your Direction</h4>
              <p>
                We may disclose information when you direct us to do so,
                authorize a connection or transaction, or otherwise provide your
                consent.
              </p>
              <h3>4. Your Privacy Choices and Rights</h3>
              <h4>4.1 Access and Correction</h4>
              <p>
                You can access and update your personal information through your
                account settings. You may also contact us to request access to
                or correction of your personal information, subject to
                applicable law.
              </p>
              <h4>4.2. Account and Data Deletion</h4>
              <p>
                You may request deletion of your Plaintr account through your
                Account Settings by selecting “Request Account Deletion.” You
                may also contact us at{" "}
                <a href="mailto:contact@Plaintr.com">contact@Plaintr.com</a> if
                you need assistance. We may verify your identity before
                processing your request.
              </p>
              <p>
                If a tax return has been prepared or filed for you through
                Plaintr, your account and related tax records may be subject to
                applicable IRS and other legal retention requirements. While the
                required retention period remains in effect, your account cannot
                be deleted and will remain accessible to you.
              </p>
              <p>
                If your account is not subject to an applicable retention
                requirement, your deletion request will be completed following
                our review. If a retention period applies, we will complete the
                deletion request after that period expires. Once completed, your
                account and associated personal information will be permanently
                deleted, except for limited information that must be retained
                for a longer period as required or permitted by law. A deleted
                account cannot be recovered or reactivated.
              </p>
              <h4>4.3. Email and Notification Preferences</h4>
              <p>
                You can opt out of promotional emails by using the unsubscribe
                link in those messages. We may continue to send service,
                security, legal, or account-related messages.
              </p>
              <p>
                Where available, you can manage Android notifications through
                your device settings.
              </p>
              <h4>4.4 Additional Privacy Rights</h4>
              <p>
                Depending on where you live, you may have additional rights
                concerning your personal information. These may include rights
                to request access, correction, deletion, or a copy of certain
                information, subject to applicable exceptions and verification
                requirements. To submit a request, contact us at
                contact@plaintr.com.
              </p>
              <h3>5. Data Retention</h3>
              <p>
                We retain tax return information and related records for the
                periods required by applicable IRS and other legal recordkeeping
                requirements, or as needed to provide the Services. Other
                personal information may be retained for as long as necessary to
                fulfill the purposes described in this Privacy Policy, unless a
                longer retention period is required or permitted by law.
              </p>
              <p>
                When an applicable retention period expires, tax return
                information and related records associated with an approved
                account deletion request will be permanently deleted, unless
                further retention is required or permitted by law.
              </p>
              <h3>6. How We Protect Information</h3>
              <p>
                Protecting your personal and financial information is important
                to Plaintr. We use reasonable administrative, technical, and
                organizational safeguards designed to protect it against
                unauthorized access, use, alteration, or disclosure. We encrypt
                personal and sensitive information while it is transmitted
                between your device, Plaintr, and the service providers that
                support the Services. However, no online service or method of
                electronic storage can be guaranteed to be completely secure.
              </p>
              <h4>7. Geographic Scope</h4>
              <p>
                The Services are intended for individuals who reside in the
                United States.
              </p>
              <h4>8. Updates to This Privacy Policy</h4>
              <p>
                We may update this Privacy Policy to reflect changes in our
                services or legal requirements, or changes to our information
                practices.
              </p>
              <p>
                When we make changes, we will update the revision date above and
                provide additional notice when required by law.
              </p>
              <h4>9. Eligibility and Children’s Privacy</h4>
              <p>
                Plaintr accounts and account-based Services, including the
                Plaintr Android application, are intended only for individuals
                who are at least 18 years old and reside in the United States.
                Plaintr does not knowingly allow individuals under 18 to create
                or control a Plaintr account. If we learn that an account was
                created or is controlled by an individual under 18, we may close
                the account and take reasonable steps to delete the associated
                personal information, subject to applicable legal and retention
                requirements.
              </p>
              <h4>10. Contact Information</h4>
              <p>
                For questions or concerns about this Privacy Policy, you can
                reach us at:
                <ol>
                  <li>
                    Email:{" "}
                    <a href="mailto:contact@plaintr.com">contact@plaintr.com</a>
                  </li>
                  <li>Legal entity: Plaintr LLC</li>
                  <li>
                    Mailing address: 228 Park Ave S #554715, New York, NY 10003
                  </li>
                </ol>
              </p>
            </div>
          )}
        </div>
      </main>
    </Fragment>
  );
}

export default Privacy;
